import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostListener,
  Input,
  OnChanges,
  Output,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { AsyncPipe, NgClass, NgFor, NgIf } from '@angular/common';
import { DropDownItem } from '../../../models/shared/stylesheet/drop-down-item';
import { createPopper, Instance } from '@popperjs/core';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { PopperUtils } from '../../../utils/popper-utils';

@Component({
  selector: 'app-typeahead-dropdown-input',
  templateUrl: './typeahead-dropdown-input.component.html',
  styleUrls: ['./typeahead-dropdown-input.component.scss'],
  standalone: true,
  imports: [AsyncPipe, NgClass, NgFor, NgIf],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TypeaheadDropdownInputComponent),
      multi: true
    }
  ],
  changeDetection: ChangeDetectionStrategy.Default
})
export class TypeaheadDropdownInputComponent implements ControlValueAccessor, OnChanges, AfterViewInit {
  @Input() dropdowns: DropDownItem[] = [];
  @Input() placeholder: string = $localize`Select`;
  @Input() isMobile: boolean = false;
  @Input() clearable: boolean = true;
  @Input() error: boolean = false;
  @Input() controlId: string | null = null;
  @Input() name: string | null = null;

  @Output() valueChanged = new EventEmitter<any>();

  constructor(private _eref: ElementRef) {}

  @ViewChild('content', { static: false }) content!: ElementRef<HTMLInputElement>;
  @ViewChild('dropdownContent', { static: false }) dropdownContent!: ElementRef<HTMLDivElement>;
  @ViewChildren('dropdownItem') dropdownItemsRef!: QueryList<ElementRef>;
  @ViewChild('select', { static: false }) select!: ElementRef<HTMLSelectElement>;

  @HostListener('document:click', ['$event'])
  public handleClickOutside(event: MouseEvent): void {
    if (!this._eref.nativeElement.contains(event.target)) {
      this.closeDropdown();
    }
  }

  private pendingValue: any = null;
  private popperInstance: Instance | undefined;
  public disabled: boolean = false;

  public activeIndex = -1;
  public dropdownItems: HTMLElement[] = [];

  public selectedItem$ = new BehaviorSubject<DropDownItem | null>(null);
  public showDropdown$ = new BehaviorSubject<boolean>(false);

  private _filteredDropdowns = new BehaviorSubject<DropDownItem[]>(this.dropdowns);
  public filteredDropdowns$ = this._filteredDropdowns as Observable<DropDownItem[]>;

  onChange = (_: any) => {};
  onTouched = () => {};

  writeValue(obj: any): void {
    this.pendingValue = obj;
    this.trySelectValue();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  public filterDropdowns(): void {
    this.showDropdown$.once(open => {
      const text = this.content.nativeElement.value.toLowerCase();
      if (text.trim().length && !open) {
        this.showDropdown$.next(true);
        this.positionDropdown();
      } else if (!text.trim().length) {
        this.showDropdown$.next(false);
      }
      this._filteredDropdowns.next(this.dropdowns.filter(it => it.title.toLowerCase().includes(text)));
    });
  }

  public toggleDropdown(): void {
    if (!this.disabled) {
      this.showDropdown$.once(val => {
        this.showDropdown$.next(!val);
        if (val) {
          this.popperInstance?.destroy();
        } else {
          this.positionDropdown();
        }
      });
    }
  }

  public closeDropdown(): void {
    this.showDropdown$.next(false);
    this.popperInstance?.destroy();
    this._filteredDropdowns.next(this.dropdowns);
    this.selectedItem$.once(item => {
      if (!!item) this.content.nativeElement.value = item.title;
    });
  }

  public itemSelected(item: DropDownItem): void {
    if (item.getSelectionIsDisabled()) return;
    this.selectedItem$.next(item);
    this.onChange(item.getSelectionValue());
    this.onTouched();
    this.closeDropdown();
    this.valueChanged.emit(item.getSelectionValue());
  }

  public resetInput(): void {
    this.selectedItem$.next(null);
    this.onChange(null);
    this.onTouched();
  }

  public onSelectChanged(event: Event): void {
    const val = (event.target as HTMLSelectElement).value;
    const selected = this.dropdowns.find(d => String(d.getSelectionValue()) === val);
    this.selectedItem$.next(selected ?? null);
    this.onChange(val);
    this.onTouched();
  }

  public onInputBlur(event: FocusEvent): void {
    const target = event.relatedTarget as HTMLElement;
    if (!target?.classList.contains('dropdown-item')) {
      setTimeout(() => {
        this.closeDropdown();
      }, 100);
    }
  }

  public onInputFocus(): void {
    this.toggleDropdown();
    this.content.nativeElement.value = '';
    combineLatest([this.showDropdown$, this.selectedItem$]).once(([show, selectedItem]) => {
      if (show) {
        this.initDropdowns();
      } else {
        if (selectedItem) {
          this.content.nativeElement.value = selectedItem.title;
        }
      }
    });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.isMobile) {
      this.closeDropdown();
      if (!changes.isMobile.firstChange) {
        this.onScreenSizeChange(this.isMobile);
      }
    }
    if (changes.dropdowns) {
      this._filteredDropdowns.next(this.dropdowns);
      this.trySelectValue();
    }
  }

  public ngAfterViewInit(): void {
    this.positionDropdown();
    this.selectedItem$.once(item => {
      if (!!this.content) this.content.nativeElement.value = item?.title ?? '';
    });
  }

  private onScreenSizeChange(isMobile: boolean): void {
    this.selectedItem$.once(item => {
      if (!item || item.value === 'null') return;
      if (isMobile) {
        this.select.nativeElement.value = item.value;
      } else {
        this.content.nativeElement.value = item.title;
      }
    });
  }

  private positionDropdown(): void {
    const dropdownElement = this.dropdownContent.nativeElement;
    this.popperInstance = createPopper(this.content.nativeElement, dropdownElement, {
      placement: 'bottom-start',
      modifiers: [
        PopperUtils.sameWidth,
        {
          name: 'offset',
          options: {
            offset: [0, 10]
          }
        },
        {
          name: 'eventListeners',
          enabled: true,
          options: {
            scroll: true
          }
        }
      ],
      strategy: 'fixed'
    });
  }

  private trySelectValue(): void {
    if (!this.dropdowns?.length || this.pendingValue === undefined) return;
    const match = this.dropdowns.find(d => d.getSelectionValue() === this.pendingValue);
    if (match) {
      this.selectedItem$.next(match);
      if (!!this.content) this.content.nativeElement.value = match.title;
    } else {
      this.selectedItem$.next(null);
      if (!!this.content) this.content.nativeElement.value = '';
    }
    setTimeout(() => (this.pendingValue = null), 0);
  }

  public handleKeyDown(event: KeyboardEvent): void {
    this.showDropdown$.once(show => {
      if (!show || this.isMobile) {
        return;
      }
    });

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.navigateDropdown(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.navigateDropdown(-1);
        break;
      case 'Enter':
        event.preventDefault();
        if (this.activeIndex >= 0 && this.activeIndex < this.dropdownItems.length) {
          this.dropdownItems[this.activeIndex].click();
        }
        break;
      case 'Escape':
        event.preventDefault();
        this.closeDropdown();
        break;
    }
  }

  private navigateDropdown(direction: number): void {
    this.dropdownItems = Array.from(this.dropdownContent.nativeElement.querySelectorAll('.dropdown-item'));
    this.activeIndex += direction;

    if (this.activeIndex < 0) {
      this.activeIndex = this.dropdownItems.length - 1;
    } else if (this.activeIndex >= this.dropdownItems.length) {
      this.activeIndex = 0;
    }

    this.dropdownItems.forEach((item, index) => {
      if (index === this.activeIndex) {
        item.classList.add('hover');
      } else {
        item.classList.remove('hover');
      }
    });

    const items = this.dropdownItemsRef.toArray();
    if (this.activeIndex >= 0 && items[this.activeIndex]) {
      items[this.activeIndex].nativeElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }

  private initDropdowns(): void {
    this.dropdownItems = Array.from(this.dropdownContent.nativeElement?.querySelectorAll('.dropdown-item'));
    this.activeIndex = -1;
  }
}
